import { createSlice } from "@reduxjs/toolkit";
import {
  organisationGroupCreateAction,
  organisationGroupDeleteAction,
  organisationGroupDetailsAction,
  organisationGroupListAction,
  organisationGroupMemberCreateAction,
  organisationGroupMemberDeleteAction,
  organisationGroupMemberDetailsAction,
  organisationGroupMemberListAction,
  organisationGroupMemberUpdateAction,
  organisationGroupUpdateAction,
} from "../Action/organisationGroupAction";
import {
  extractOrganisationGroupDetail,
  extractOrganisationGroupList,
  extractOrganisationGroupListMeta,
  extractOrganisationGroupMemberDetail,
  extractOrganisationGroupMemberList,
  extractOrganisationGroupMemberListMeta,
} from "../../Util/organisationGroupUtils";

const initialState = {
  organisationGroups: [],
  organisationGroupsRaw: null,
  organisationGroupListMeta: null,
  organisationGroupDetails: null,
  organisationGroupDetailsRaw: null,
  organisationGroupMembers: [],
  organisationGroupMembersRaw: null,
  organisationGroupMemberListMeta: null,
  organisationGroupMemberDetails: null,
  organisationGroupMemberDetailsRaw: null,
  listLoading: false,
  detailsLoading: false,
  memberListLoading: false,
  memberDetailsLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  memberCreateLoading: false,
  memberUpdateLoading: false,
  memberDeleteLoading: false,
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  memberCreateStatus: "idle",
  memberUpdateStatus: "idle",
  memberDeleteStatus: "idle",
  error: null,
};

const organisationGroupSlice = createSlice({
  name: "organisationGroup",
  initialState,
  reducers: {
    resetOrganisationGroupMutationState: (state) => {
      state.createStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
      state.memberCreateStatus = "idle";
      state.memberUpdateStatus = "idle";
      state.memberDeleteStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(organisationGroupListAction.pending, (state) => {
      state.listLoading = true;
      state.error = null;
    });

    builder.addCase(organisationGroupListAction.fulfilled, (state, action) => {
      state.listLoading = false;
      state.organisationGroupsRaw = action.payload;
      state.organisationGroups = extractOrganisationGroupList(action.payload);
      state.organisationGroupListMeta = extractOrganisationGroupListMeta(
        action.payload,
        state.organisationGroups,
      );
    });

    builder.addCase(organisationGroupListAction.rejected, (state, action) => {
      state.listLoading = false;
      state.error = action.payload || action.error;
    });

    builder.addCase(organisationGroupDetailsAction.pending, (state) => {
      state.detailsLoading = true;
      state.error = null;
    });

    builder.addCase(
      organisationGroupDetailsAction.fulfilled,
      (state, action) => {
        state.detailsLoading = false;
        state.organisationGroupDetailsRaw = action.payload;
        state.organisationGroupDetails = extractOrganisationGroupDetail(
          action.payload,
        );
      },
    );

    builder.addCase(
      organisationGroupDetailsAction.rejected,
      (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload || action.error;
      },
    );

    builder.addCase(organisationGroupMemberListAction.pending, (state) => {
      state.memberListLoading = true;
      state.error = null;
    });

    builder.addCase(
      organisationGroupMemberListAction.fulfilled,
      (state, action) => {
        state.memberListLoading = false;
        state.organisationGroupMembersRaw = action.payload;
        state.organisationGroupMembers = extractOrganisationGroupMemberList(
          action.payload,
        );
        state.organisationGroupMemberListMeta =
          extractOrganisationGroupMemberListMeta(
            action.payload,
            state.organisationGroupMembers,
          );
      },
    );

    builder.addCase(
      organisationGroupMemberListAction.rejected,
      (state, action) => {
        state.memberListLoading = false;
        state.error = action.payload || action.error;
      },
    );

    builder.addCase(organisationGroupMemberDetailsAction.pending, (state) => {
      state.memberDetailsLoading = true;
      state.error = null;
    });

    builder.addCase(
      organisationGroupMemberDetailsAction.fulfilled,
      (state, action) => {
        state.memberDetailsLoading = false;
        state.organisationGroupMemberDetailsRaw = action.payload;
        state.organisationGroupMemberDetails =
          extractOrganisationGroupMemberDetail(action.payload);
      },
    );

    builder.addCase(
      organisationGroupMemberDetailsAction.rejected,
      (state, action) => {
        state.memberDetailsLoading = false;
        state.error = action.payload || action.error;
      },
    );

    builder.addCase(organisationGroupCreateAction.pending, (state) => {
      state.createLoading = true;
      state.createStatus = "pending";
      state.error = null;
    });

    builder.addCase(organisationGroupCreateAction.fulfilled, (state) => {
      state.createLoading = false;
      state.createStatus = "succeeded";
    });

    builder.addCase(organisationGroupCreateAction.rejected, (state, action) => {
      state.createLoading = false;
      state.createStatus = "failed";
      state.error = action.payload || action.error;
    });

    builder.addCase(organisationGroupUpdateAction.pending, (state) => {
      state.updateLoading = true;
      state.updateStatus = "pending";
      state.error = null;
    });

    builder.addCase(organisationGroupUpdateAction.fulfilled, (state) => {
      state.updateLoading = false;
      state.updateStatus = "succeeded";
    });

    builder.addCase(organisationGroupUpdateAction.rejected, (state, action) => {
      state.updateLoading = false;
      state.updateStatus = "failed";
      state.error = action.payload || action.error;
    });

    builder.addCase(organisationGroupDeleteAction.pending, (state) => {
      state.deleteLoading = true;
      state.deleteStatus = "pending";
      state.error = null;
    });

    builder.addCase(organisationGroupDeleteAction.fulfilled, (state) => {
      state.deleteLoading = false;
      state.deleteStatus = "succeeded";
    });

    builder.addCase(organisationGroupDeleteAction.rejected, (state, action) => {
      state.deleteLoading = false;
      state.deleteStatus = "failed";
      state.error = action.payload || action.error;
    });

    builder.addCase(organisationGroupMemberUpdateAction.pending, (state) => {
      state.memberUpdateLoading = true;
      state.memberUpdateStatus = "pending";
      state.error = null;
    });

    builder.addCase(organisationGroupMemberCreateAction.pending, (state) => {
      state.memberCreateLoading = true;
      state.memberCreateStatus = "pending";
      state.error = null;
    });

    builder.addCase(organisationGroupMemberCreateAction.fulfilled, (state) => {
      state.memberCreateLoading = false;
      state.memberCreateStatus = "succeeded";
    });

    builder.addCase(
      organisationGroupMemberCreateAction.rejected,
      (state, action) => {
        state.memberCreateLoading = false;
        state.memberCreateStatus = "failed";
        state.error = action.payload || action.error;
      },
    );

    builder.addCase(organisationGroupMemberUpdateAction.fulfilled, (state) => {
      state.memberUpdateLoading = false;
      state.memberUpdateStatus = "succeeded";
    });

    builder.addCase(
      organisationGroupMemberUpdateAction.rejected,
      (state, action) => {
        state.memberUpdateLoading = false;
        state.memberUpdateStatus = "failed";
        state.error = action.payload || action.error;
      },
    );

    builder.addCase(organisationGroupMemberDeleteAction.pending, (state) => {
      state.memberDeleteLoading = true;
      state.memberDeleteStatus = "pending";
      state.error = null;
    });

    builder.addCase(organisationGroupMemberDeleteAction.fulfilled, (state) => {
      state.memberDeleteLoading = false;
      state.memberDeleteStatus = "succeeded";
    });

    builder.addCase(
      organisationGroupMemberDeleteAction.rejected,
      (state, action) => {
        state.memberDeleteLoading = false;
        state.memberDeleteStatus = "failed";
        state.error = action.payload || action.error;
      },
    );
  },
});

export const { resetOrganisationGroupMutationState } =
  organisationGroupSlice.actions;

export default organisationGroupSlice.reducer;
